import { useState, useEffect } from 'react';

export function usePhoneNumberInput(initialPhoneNumber: string) {
    const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
    const [phoneError, setPhoneError] = useState('');

    // Format phone number with +1 by default if empty
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (phoneNumber === '') {
            setPhoneNumber('+1 ');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Phone number validation and formatting handler
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const handlePhoneChange = (e: { target: { value: any; }; }) => {
        const value = e.target.value;
        
        // Allow only +, numbers, spaces, dashes, and parentheses for input
        if (/^[+0-9\s\-()]*$/.test(value)) {
            // Extract just the digits for formatting
            const digitsOnly = value.replace(/\D/g, '');
            
            // If they've deleted everything, reset to +1
            if (value === '' || value === '+') {
                setPhoneNumber('+1 ');
                setPhoneError('');
                return;
            }
            
            // Always ensure it starts with +1
            let formattedNumber = '';
            
            // Force the first digit to be 1 (US country code)
            let normalizedDigits = digitsOnly;
            if (normalizedDigits.length > 0 && normalizedDigits.charAt(0) !== '1') {
                normalizedDigits = `1${normalizedDigits}`;
            }
            
            // Format based on the number of digits
            if (normalizedDigits.length <= 1) {
                // Just the country code
                formattedNumber = `+${normalizedDigits}`;
            } else if (normalizedDigits.length <= 4) {
                // Country code + start of area code
                formattedNumber = `+${normalizedDigits.charAt(0)} (${normalizedDigits.substring(1, normalizedDigits.length)}`;
            } else if (normalizedDigits.length <= 7) {
                // Country code + full area code + start of prefix
                formattedNumber = `+${normalizedDigits.charAt(0)} (${normalizedDigits.substring(1, 4)}) ${normalizedDigits.substring(4, normalizedDigits.length)}`;
            } else {
                // Country code + area code + prefix + line number
                formattedNumber = `+${normalizedDigits.charAt(0)} (${normalizedDigits.substring(1, 4)}) ${normalizedDigits.substring(4, 7)}-${normalizedDigits.substring(7, Math.min(11, normalizedDigits.length))}`;
            }
            
            setPhoneNumber(formattedNumber);
            
            // Validate phone number format
            if (normalizedDigits.length !== 11 && normalizedDigits.length > 1) {
                // Only show error if they've started typing and haven't finished
                if (normalizedDigits.length < 11) {
                    setPhoneError('Please enter a complete 10-digit US phone number');
                }
            } else {
                setPhoneError('');
            }
        }
    };

    // Parse the phone number into E.164 format for Firebase
    const getFirebasePhoneNumber = (): string => {
        // Remove all non-digit characters except the leading +
        const parsedNumber = phoneNumber.replace(/(?!^\+)\D/g, '');
        
        // Ensure we have a valid E.164 format with + and country code
        if (parsedNumber.startsWith('+')) {
            return parsedNumber;
        }
        
        if (parsedNumber.startsWith('1')) {
            return `+${parsedNumber}`;
        }
        
        return `+1${parsedNumber}`;
    };

    return {
        phoneNumber,
        phoneError,
        isValidPhoneNumber: phoneError === '',
        handlePhoneChange,
        setPhoneNumber,
        getFirebasePhoneNumber
    };
}